import React from 'react'
import styled from 'styled-components'

import {CardsCarousel} from '@/components/cards-carousel'
import {EventCard} from '@/components/event-card'
import Image from '@/components/image'
import PrismicLink from '@/components/prismic-link'
import {Text, TextLink} from '@/components/text'
import type {ResourceSliderFragment, ResourceSliderSecondaryFragment} from '@/graphql-types'
import {Container, Section} from '@/styles/grid'
import {tokens} from '@/theme'
import mq from '@/utils/media-query'

interface Props {
    slice: ResourceSliderFragment | ResourceSliderSecondaryFragment
}

export const ResourceSlider = React.forwardRef<HTMLDivElement, Props>(({slice}, ref) => {
    const eventData = slice.items.map((card) => {
        return {
            title: card.card_title,
            description: card.card_description,
            category: card.category,
            language: card.language,
            withBorder: false,
            Image: () => <Image image={card.card_image} />,
            CTA: (props: React.ComponentProps<typeof PrismicLink>) => (
                <PrismicLink {...props} {...card.to}>
                    {card.card_cta}
                </PrismicLink>
            ),
        }
    })

    return (
        <Section edgeToEdge>
            <Title ref={ref}>
                <Text as="h2" $variant="heading-base">
                    {slice.primary.title}
                </Text>
                <ViewAllLink
                    $variant="paragraph-large"
                    as={PrismicLink}
                    {...slice.primary.top_link}
                >
                    {slice.primary.top_link_text}
                </ViewAllLink>
                <Text $color="muted">{slice.primary.description}</Text>
            </Title>
            <CardsCarousel>
                {eventData.map((event) => (
                    <EventCard key={event.title} {...event} />
                ))}
            </CardsCarousel>
            <MobileViewAllLink
                $variant="paragraph-large"
                as={PrismicLink}
                {...slice.primary.top_link}
            >
                {slice.primary.top_link_text}
            </MobileViewAllLink>
        </Section>
    )
})

const Title = styled(Container)`
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: ${tokens.spacing20};
    margin-bottom: ${tokens.spacing20};

    ${mq.min('sm')} {
        grid-template-columns: 1fr 1fr;
        margin-bottom: ${tokens.spacing40};
    }
`

const ViewAllLink = styled(TextLink)`
    display: none;

    ${mq.min('sm')} {
        display: block;
        justify-self: end;
        height: fit-content;
    }
`

const MobileViewAllLink = styled(TextLink)`
    display: inline-block;
    margin-top: ${tokens.spacing20};
    margin-left: ${tokens.spacing24};

    ${mq.min('sm')} {
        display: none;
    }
`
